/* eslint-disable @typescript-eslint/no-explicit-any */

import type { ChipJSON, Point3 } from '@/library/step-generator/model/types'
import { getOC } from '@/library/occt/getOC'
import * as CAD from '@/library/occt/cad'
import { buildShapeToOcct } from '@/library/step-generator/shapeToOcct'

function optimizedUnion(oc: any, shapes: any[]): any {
  if (shapes.length === 0) return null
  let currentList = [...shapes]
  while (currentList.length > 1) {
    const nextList = []
    for (let i = 0; i < currentList.length; i += 2) {
      if (i + 1 < currentList.length) {
        try {
          nextList.push(CAD.fuse(oc, currentList[i], currentList[i + 1]))
        } catch (e) {
          console.warn(`[STEP] Failed to fuse shapes pairwise, keeping one:`, e)
          nextList.push(currentList[i])
        }
      } else {
        nextList.push(currentList[i])
      }
    }
    currentList = nextList
  }
  return currentList[0]
}

export async function exportStepFromChipJson(chip: ChipJSON): Promise<Blob> {
  const oc = await getOC()

  // Fluid3D 内部用 µm，STEP 标准用 mm，缩放 1/1000
  const S = 1 / 1000

  // chip base
  let result = CAD.makeBox(
    oc,
    chip.general.length * S,
    chip.general.width * S,
    chip.general.thickness * S
  )
  console.log('[STEP] base box:', chip.general.length * S, '×', chip.general.width * S, '×', chip.general.thickness * S)

  let shapeIdx = 0
  const negativeShapes: any[] = []
  const positiveShapes: any[] = []

  function collectShape(label: string, shape: any) {
    try {
      const solid = buildShapeToOcct(oc, shape, 0, S)
      
      const fillValue = shape.fill;
      // In flui3d-mold, undefined fill means regular channel geometry, which MUST be a positive ridge (additive).
      // Only explicitly fill === false geometries (like pad holes) should be subtracted.
      const shouldSubtract = fillValue === false;
      
      if (shouldSubtract) {
        negativeShapes.push(solid)
      } else {
        positiveShapes.push(solid)
      }
      
      console.log(`[STEP] #${shapeIdx} ${label} type=${shape.type} subtract=${shouldSubtract} -> solid OK`)
    } catch (e) {
      console.error(`[STEP] #${shapeIdx} ${label} type=${shape.type} BUILD FAILED:`, e)
    }
    shapeIdx++
  }

  for (const [li, layer] of chip.layers.entries()) {
    for (const [ci, channel] of layer.channels.entries()) {
      for (const shape of channel.shapes) {
        collectShape(`layer[${li}].channel[${ci}]`, shape)
      }
    }

    for (const [ci, comp] of layer.components.entries()) {
      for (const shape of comp.shapes) {
        collectShape(`layer[${li}].comp[${ci}].shape`, shape)
      }

      for (const [chi, ch] of comp.channels.entries()) {
        for (const shape of ch.shapes) {
          collectShape(`layer[${li}].comp[${ci}].channel[${chi}]`, shape)
        }
      }
    }

    if (layer.compensation) {
      try {
        const compSolid = CAD.makePolygonPrism(
          oc,
          layer.compensation.points.map((p: Point3) => ({
            x: p.x * S,
            y: p.y * S,
            z: p.z * S,
          })),
          { x: layer.compensation.direction.x * S, y: layer.compensation.direction.y * S, z: layer.compensation.direction.z * S }
        )
        const fillValue = layer.compensation.fill
        const shouldSubtract = fillValue === false
        if (shouldSubtract) {
          negativeShapes.push(compSolid)
        } else {
          positiveShapes.push(compSolid)
        }
        console.log(`[STEP] layer[${li}] compensation collected OK (subtract=${shouldSubtract})`)
      } catch (e) {
        console.error(`[STEP] layer[${li}] compensation FAILED:`, e)
      }
    }
  }

  for (const [ci, conn] of chip.crosslayerConnections.entries()) {
    for (const shape of conn.shapes) {
      collectShape(`crosslayer[${ci}]`, shape)
    }
  }

  console.log(`[STEP] Shapes collected: ${negativeShapes.length} negative, ${positiveShapes.length} positive`)

  // 1. Add back the positive shapes (pillars)
  if (positiveShapes.length > 0) {
    try {
      console.log('[STEP] Fusing all positive tool shapes into a single valid solid...')
      const masterAdd = optimizedUnion(oc, positiveShapes)
      if (masterAdd) {
        console.log('[STEP] Fusing positive features back to the main block...')
        result = CAD.fuse(oc, result, masterAdd)
      }
    } catch (e) {
      console.error('[STEP] Fusing or Add of positive shapes FAILED:', e)
    }
  }

  // 2. Subtract the negative shapes (holes/channels)
  if (negativeShapes.length > 0) {
    try {
      console.log('[STEP] Fusing all negative tool shapes into a single valid solid...')
      const masterTool = optimizedUnion(oc, negativeShapes)
      if (masterTool) {
        console.log('[STEP] Executing monolithic Cut with the fused tool...')
        result = CAD.cut(oc, result, masterTool)
      }
    } catch (e) {
      console.error('[STEP] Fusing or Cut of negative shapes FAILED:', e)
    }
  }

  try {
    console.log('[STEP] Executing topological cleanup (UnifySameDomain)...')
    result = CAD.unifyShape(oc, result)
    console.log('[STEP] Shape processing complete.')
  } catch (e) {
    console.error('[STEP] Topological cleanup FAILED:', e)
  }

  return CAD.exportSTEP(oc, result, 'fluid3d.step')
}